import createClient from 'openapi-fetch';
import type { Middleware } from 'openapi-fetch';
import createQueryClient from 'openapi-react-query';

import { env } from '@/shared/config';

import type { paths } from './schema';
import {
  API_BASE_URL,
  authHeader,
  expireSession,
  getAccessToken,
  isPublicAuthPage,
  refreshSession,
} from './session';

/**
 * Типобезопасный клиент по сгенерированной схеме бэкенда.
 *
 *   const { data } = useQuery('get', '/tasks/{id}', { params: { path: { id } } });
 *   const { mutate } = useMutation('post', '/tasks/add');
 *
 * Путь, метод, параметры и тело проверяются компилятором, тип ответа выводится
 * из спеки. Рядом продолжает жить axios-инстанс `http` — старые фетчеры на нём.
 */

/** Подставляет Bearer нативной сессии, как это делает axios-инстанс. */
const authMiddleware: Middleware = {
  onRequest({ request }) {
    const header = authHeader();
    if (header) {
      request.headers.set('Authorization', header.Authorization);
    }
    return request;
  },

  /**
   * Повторяет поведение response-интерсептора `http`: на 401 один раз пробуем
   * обновить токены и повторить запрос, иначе — разлогин.
   */
  async onResponse({ request, response }) {
    if (response.status !== 401 || env.authBypass || isPublicAuthPage()) {
      return response;
    }

    if (!getAccessToken()) {
      expireSession();
      return response;
    }

    // Повтор помечаем заголовком: у Request нет своего места под метаданные.
    // Второй 401 уже с обновлённым токеном — это отказ конкретной ручки, а не
    // конец сессии (см. тот же случай в http.ts). Разлогинивать не за что.
    if (request.headers.get('X-Retried-After-Refresh')) {
      return response;
    }

    const token = await refreshSession();
    if (!token) {
      return response;
    }

    const retry = request.clone();
    retry.headers.set('Authorization', `Bearer ${token}`);
    retry.headers.set('X-Retried-After-Refresh', '1');

    return fetch(retry);
  },
};

const client = createClient<paths>({
  baseUrl: API_BASE_URL,
  // Личность — Bearer нативной сессии (см. authMiddleware) либо кука jwt_token
  // старой сессии Bitrix24.
  credentials: 'include',
});

client.use(authMiddleware);

export const { useQuery, useMutation, useSuspenseQuery, queryOptions } =
  createQueryClient<paths>(client);

export default client;
